import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { getSettings, changeRate, getRateHistory } from "@/lib/api";

export default function RateSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ rate: 25, serviceCharges: 50, conversionFactor: 1, effectiveFrom: new Date().toISOString().split("T")[0] });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const load = async () => {
      const s = await getSettings();
      setSettings(s);
      setForm((f) => ({ ...f, rate: s.rate || 25, serviceCharges: s.serviceCharges || 50, conversionFactor: s.conversionFactor || 1 }));
      try { setHistory(await getRateHistory()); } catch (e) { console.warn("Failed to load rate history:", e.message); }
    };
    load();
  }, []);

  const handleSave = async () => {
    await changeRate(form);
    const s = await getSettings();
    setSettings(s);
    try { setHistory(await getRateHistory()); } catch (e) { console.warn("Failed to refresh rate history:", e.message); }
    toast.success("Rate updated successfully. New bills will use the updated rate.");
  };

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1200px] mx-auto" data-testid="rate-settings-page">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="outline" onClick={() => navigate(-1)} className="rounded-sm border-zinc-300"><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-zinc-900">
          <TrendingUp className="w-8 h-8 inline mr-2 text-orange-600" />Rate Settings
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current Rate Card */}
        <Card className="border border-zinc-200 rounded-sm shadow-none">
          <CardHeader><CardTitle className="text-xs font-bold uppercase tracking-wider text-zinc-500">Current Active Rates</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-orange-50 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase font-bold mb-1">Rate/KG</p>
                <p className="text-2xl font-heading font-black text-orange-600" data-testid="current-rate">₹{(settings.rate || 0).toFixed(2)}</p>
              </div>
              <div className="text-center p-4 bg-zinc-50 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase font-bold mb-1">Service</p>
                <p className="text-2xl font-heading font-black" data-testid="current-service">₹{(settings.serviceCharges || 0).toFixed(2)}</p>
              </div>
              <div className="text-center p-4 bg-zinc-50 rounded-sm">
                <p className="text-xs text-zinc-500 uppercase font-bold mb-1">Conv. Factor</p>
                <p className="text-2xl font-heading font-black" data-testid="current-conv">{(settings.conversionFactor || 0).toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Update Rate Card */}
        <Card className="border border-zinc-200 rounded-sm shadow-none">
          <CardHeader><CardTitle className="text-xs font-bold uppercase tracking-wider text-zinc-500">Update Rates</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Rate per KG</Label><Input data-testid="new-rate-input" type="number" step="0.01" value={form.rate} onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) || 0 })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Service Charges</Label><Input data-testid="new-service-input" type="number" step="0.01" value={form.serviceCharges} onChange={(e) => setForm({ ...form, serviceCharges: parseFloat(e.target.value) || 0 })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Conversion Factor</Label><Input data-testid="new-conv-input" type="number" step="0.01" value={form.conversionFactor} onChange={(e) => setForm({ ...form, conversionFactor: parseFloat(e.target.value) || 0 })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
              <div><Label className="text-xs">Effective From</Label><Input data-testid="effective-from-input" type="date" value={form.effectiveFrom} onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} className="bg-zinc-50 border-zinc-300 rounded-sm mt-1" /></div>
            </div>
            <p className="text-[10px] text-zinc-400">Old bills will NOT be affected. Only new bills will use updated rates.</p>
            <Button data-testid="save-rate-button" onClick={handleSave} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-sm">Update Rates</Button>
          </CardContent>
        </Card>
      </div>

      {/* Rate Change History */}
      <Card className="border border-zinc-200 rounded-sm shadow-none mt-6">
        <CardHeader><CardTitle className="text-xs font-bold uppercase tracking-wider text-zinc-500">Rate Change History</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50">
                <TableHead className="text-xs font-bold uppercase text-zinc-500">Date</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500">Changed By</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Old Rate</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">New Rate</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">Old Service</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500 text-right">New Service</TableHead>
                <TableHead className="text-xs font-bold uppercase text-zinc-500">Effective From</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center text-zinc-400 py-8">No rate changes recorded.</TableCell></TableRow>
              ) : history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell className="text-xs font-mono">{new Date(h.changedAt).toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-sm">{h.changedBy}</TableCell>
                  <TableCell className="text-sm font-mono text-right">₹{(h.oldRate || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-sm font-mono text-right font-bold text-orange-600">₹{(h.newRate || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-sm font-mono text-right">₹{(h.oldServiceCharges || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-sm font-mono text-right font-bold">₹{(h.newServiceCharges || 0).toFixed(2)}</TableCell>
                  <TableCell className="text-sm">{h.effectiveFrom}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
